import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { MudCarpFish } from "@/components/MudCarpFish";
import { Bubbles } from "@/components/Bubbles";
import { api, type SaleStatus } from "@/lib/api";
import { formatPln } from "@/lib/utils";

const POLL_INTERVAL_MS = 2500;
const MAX_POLLS = 60; // ~150s

export function Thanks() {
  const { id: routeId } = useParams<{ id: string }>();
  const [search] = useSearchParams();

  // Three accepted shapes (priority):
  //  /thanks/<guid>          — explicit Sale ID in path
  //  /thanks?id=<guid>       — explicit Sale ID in query
  //  /thanks?session=<cs_..> — Stripe redirect (CHECKOUT_SESSION_ID), needs lookup
  const saleId = routeId || search.get("id") || "";
  const providerSession = search.get("session") || "";
  const lookupKey = saleId || providerSession;

  const reduced = useReducedMotion();

  const [sale, setSale] = useState<SaleStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [polls, setPolls] = useState(0);

  const status = sale?.status ?? "Created";
  const isFinal = useMemo(
    () => status === "Answered" || status === "Invoiced" || status === "Failed" || status === "Cancelled",
    [status],
  );

  useEffect(() => {
    if (!lookupKey) {
      setError("Brak ID sprzedaży w URL.");
      return;
    }
    let cancelled = false;
    let timer: number | null = null;

    async function tick() {
      try {
        const s = saleId
          ? await api.getSale(saleId)
          : await api.getSaleByProviderSession(providerSession);
        if (cancelled) return;
        setSale(s);
        setPolls((p) => p + 1);
        if (
          s.status !== "Answered" &&
          s.status !== "Invoiced" &&
          s.status !== "Failed" &&
          s.status !== "Cancelled" &&
          polls < MAX_POLLS
        ) {
          timer = window.setTimeout(tick, POLL_INTERVAL_MS);
        }
      } catch (raw) {
        if (cancelled) return;
        const msg =
          (raw as { message?: string })?.message ||
          "Nie znaleźliśmy tej sprzedaży. Może źle skopiowane?";
        setError(msg);
      }
    }
    tick();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupKey]);

  return (
    <div className="relative">
      <Bubbles count={14} />
      <section className="relative max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <div className="brut bg-carp-paper p-6 sm:p-10">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <p className="chip">DZIĘKUJEMY ZA HAJS</p>
            <p className="chip bg-carp-ink text-carp-paper">
              ID: <span className="font-mono">{(sale?.saleId || saleId).slice(0, 8) || "—"}</span>
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-5 flex justify-center">
              <FishStage status={status} />
            </div>
            <div className="lg:col-span-7">
              <AnimatePresence mode="wait">
                {error ? (
                  <motion.div
                    key="err"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <h1 className="font-display font-bold text-4xl sm:text-5xl">
                      Coś się rozjechało.
                    </h1>
                    <p className="mt-3 text-lg">{error}</p>
                    <Link to="/buy" className="btn-primary mt-6">
                      Spróbuj ponownie
                    </Link>
                  </motion.div>
                ) : !sale ? (
                  <motion.div
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <h1 className="font-display font-bold text-4xl sm:text-5xl">
                      Łapię puls karpia…
                    </h1>
                    <p className="mt-3 text-lg text-carp-ink/80">
                      Dzwonimy do backendu. Karp zaraz coś palnie.
                    </p>
                    <SkeletonLines />
                  </motion.div>
                ) : sale.status === "Answered" || sale.status === "Invoiced" ? (
                  <Result key="answered" sale={sale} celebrate={!reduced} />
                ) : sale.status === "Failed" ? (
                  <motion.div
                    key="failed"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <h1 className="font-display font-bold text-4xl sm:text-5xl text-carp-destructive">
                      Karp się zaciął.
                    </h1>
                    <p className="mt-3 text-lg">
                      Coś nie zagrało po stronie naszej. Pieniądze są bezpieczne — odezwiemy się.
                    </p>
                    <Link to="/buy" className="btn-primary mt-6">
                      Wracam do formularza
                    </Link>
                  </motion.div>
                ) : (
                  <motion.div
                    key="waiting"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <h1 className="font-display font-bold text-4xl sm:text-5xl">
                      Karp wciąga powietrze…
                    </h1>
                    <p className="mt-3 text-lg text-carp-ink/80">
                      Status: <strong>{status}</strong>. Wiadomość jest w drodze do urządzenia.
                    </p>
                    <p className="text-sm mt-2 text-carp-ink/60 font-mono">
                      Sprawdzamy co {POLL_INTERVAL_MS / 1000}s — odświeżenie {polls}/{MAX_POLLS}
                    </p>
                    <SkeletonLines />
                  </motion.div>
                )}
              </AnimatePresence>

              {sale && !isFinal && polls >= MAX_POLLS && (
                <p className="mt-6 text-sm text-carp-ink/70">
                  Bierze dłużej niż zwykle. Odśwież stronę za chwilę albo napisz do nas.
                </p>
              )}
            </div>
          </div>
        </div>

        {sale && (
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            <DetailCard label="Kwota" value={formatPln(sale.amount)} tone="rose" />
            <DetailCard label="Status" value={sale.status} tone="blue" />
            <DetailCard
              label="Mail"
              value={sale.email}
              tone="yellow"
              mono
            />
          </div>
        )}

        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/" className="btn-ghost">
            Strona główna
          </Link>
          <Link to="/buy" className="btn-primary">
            Zagaduj jeszcze raz
          </Link>
        </div>
      </section>
    </div>
  );
}

function FishStage({ status }: { status: SaleStatus["status"] }) {
  const intensity =
    status === "Answered" || status === "Invoiced"
      ? 4
      : status === "Paid"
        ? 3
        : status === "Pending"
          ? 1
          : 0;
  const talking = status === "Paid";
  return (
    <div className="brut bg-carp-paper p-6 -rotate-2 max-w-sm w-full">
      <MudCarpFish intensity={intensity} talking={talking} size={0.75} ariaHidden={false} />
      <p className="mt-2 text-xs font-mono text-carp-ink/70">
        <span className="text-carp-lime">●</span> live ESP32 ↔ backend
      </p>
    </div>
  );
}

function Result({ sale, celebrate }: { sale: SaleStatus; celebrate: boolean }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
      <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">
        Karp powiedział:
      </h1>
      <motion.blockquote
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4, type: "spring", stiffness: 220, damping: 18 }}
        className="mt-4 brut bg-carp-yellow p-5 sm:p-6 text-2xl sm:text-3xl font-display font-bold leading-tight text-carp-ink"
      >
        „{sale.responseText || "..."}"
      </motion.blockquote>
      <p className="mt-3 text-sm text-carp-ink/70 font-mono">
        Wypowiedziane: {sale.answeredAt ? new Date(sale.answeredAt).toLocaleString("pl-PL") : "—"}
      </p>

      {celebrate && <Confetti />}
    </motion.div>
  );
}

function DetailCard({
  label,
  value,
  tone,
  mono,
}: {
  label: string;
  value: string;
  tone: "rose" | "blue" | "yellow";
  mono?: boolean;
}) {
  const bg =
    tone === "rose"
      ? "bg-carp-secondary text-carp-ink"
      : tone === "blue"
        ? "bg-carp-accent text-white"
        : "bg-carp-yellow text-carp-ink";
  return (
    <div className={`brut p-4 ${bg}`}>
      <div className="text-xs font-display font-bold uppercase tracking-wide opacity-80">{label}</div>
      <div className={`font-display font-bold text-xl mt-1 ${mono ? "font-mono text-base break-all" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function SkeletonLines() {
  return (
    <div className="mt-6 space-y-3" aria-hidden>
      <div className="h-4 bg-carp-muted animate-pulse w-3/4" />
      <div className="h-4 bg-carp-muted animate-pulse w-1/2" />
      <div className="h-4 bg-carp-muted animate-pulse w-5/6" />
    </div>
  );
}

function Confetti() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 28 }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.4,
        rotate: Math.random() * 360,
        color: ["#E11D48", "#2563EB", "#FACC15", "#84CC16", "#22D3EE"][i % 5],
        size: 8 + Math.random() * 12,
      })),
    [],
  );
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 overflow-hidden z-50">
      {pieces.map((p) => (
        <motion.span
          key={p.id}
          initial={{ y: -40, x: 0, opacity: 1, rotate: 0 }}
          animate={{ y: "110vh", rotate: p.rotate }}
          transition={{ duration: 1.6 + Math.random() * 1.4, delay: p.delay, ease: "easeIn" }}
          style={{
            left: `${p.left}%`,
            width: p.size,
            height: p.size * 0.4,
            background: p.color,
            border: "2px solid #1A0A12",
          }}
          className="absolute top-0"
        />
      ))}
    </div>
  );
}

export default Thanks;
